import hoistNonReactStatics from 'hoist-non-react-statics';
import { ComponentType, createContext, ReactNode, useContext, VFC } from 'react';
import getDisplayName from 'react-display-name';

export class FeatureFlags {
  static enabledFlags: string[] = window.ciq?.enabledFeatureFlags || [];

  static isFeatureEnabled(flagName: string): boolean {
    const ffEnabled = FeatureFlags.enabledFlags.includes(flagName);
    if (window.DD_RUM) {
      window.DD_RUM?.addFeatureFlagEvaluation(flagName, ffEnabled);
    }
    return ffEnabled;
  }
}

export type FeatureFlagsContextType = {
  enabledFeatureFlags: string[];
  isFeatureEnabled: (featureName: string) => boolean;
};

const createDefaultContext = (): FeatureFlagsContextType => ({
  enabledFeatureFlags: FeatureFlags.enabledFlags,
  isFeatureEnabled: FeatureFlags.isFeatureEnabled,
});

const FeatureFlagsContext = createContext<FeatureFlagsContextType>(createDefaultContext());
FeatureFlagsContext.displayName = 'FeatureFlagsContext';

export const FeatureFlagsProvider = ({
  enabledFeatureFlags = [],
  children,
}: {
  enabledFeatureFlags?: string[];
  children: ReactNode;
}) => {
  if (FeatureFlagsProvider.instancesCount > 0) {
    throw new Error('Only one instance of FeatureFlagsProvider is allowed!');
  }

  if (enabledFeatureFlags.length > 0) {
    FeatureFlags.enabledFlags = enabledFeatureFlags;
  }

  FeatureFlagsProvider.instancesCount += 1;

  // Reload context value in case new flags were added
  return <FeatureFlagsContext.Provider value={createDefaultContext()}>{children}</FeatureFlagsContext.Provider>;
};

FeatureFlagsProvider.instancesCount = 0;

export const useFeatureFlags = () => useContext(FeatureFlagsContext);
export const useIsFeatureEnabled = (featureName: string) =>
  useContext(FeatureFlagsContext).isFeatureEnabled(featureName);

export function withFeatureFlags<Props extends FeatureFlagsContextType>(WrappedComponent: ComponentType<Props>) {
  const displayName = `WithFeatureFlags(${getDisplayName(WrappedComponent)})`;

  const WithFeatureFlags: VFC<Omit<Props, keyof FeatureFlagsContextType>> = (props) => {
    return (
      <FeatureFlagsContext.Consumer>
        {(values) => <WrappedComponent {...(props as Props)} {...values} />}
      </FeatureFlagsContext.Consumer>
    );
  };

  WithFeatureFlags.displayName = displayName;

  hoistNonReactStatics(WithFeatureFlags, WrappedComponent);

  return WithFeatureFlags;
}
